import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  TooltipProvider,
} from '@design-system';
import type { FeatureFlagOp } from '@shared';
import { isKnownFlagKey, KNOWN_FLAG_KEYS } from '../utils/known-flag-keys';
import { useAdvisorBatchLookup } from '../hooks/use-advisor-batch-lookup';
import { AddAdvisorPopover } from './AddAdvisorPopover';
import { LegacyFlagWarningIcon } from './LegacyFlagWarningIcon';

type AdvisorCentricViewProps = {
  originalConfig: AppConfigSchema;
  targetConfig: AppConfigSchema;
  onEnqueueOp: (op: FeatureFlagOp) => void;
  isSaving?: boolean;
};

export function AdvisorCentricView({
  originalConfig,
  targetConfig,
  onEnqueueOp,
  isSaving,
}: AdvisorCentricViewProps) {
  const advisorIds = useMemo(
    () => Object.keys(targetConfig.advisors ?? {}).sort(),
    [targetConfig],
  );

  const { lookups } = useAdvisorBatchLookup(advisorIds);
  const lookupByAdvisorId = useMemo(() => {
    const map = new Map<string, (typeof lookups)[number]>();
    for (const l of lookups) map.set(l.advisorId, l);
    return map;
  }, [lookups]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="max-w-4xl space-y-2">
        <div className="flex justify-end">
          <AddAdvisorPopover
            existingAdvisorIds={advisorIds}
            onEnqueueOp={onEnqueueOp}
            disabled={isSaving}
          />
        </div>
        {advisorIds.length === 0 && (
          <Card className="p-6 text-center">
            <p className="text-muted-foreground text-sm">
              No advisor overrides in current AppConfig.
            </p>
          </Card>
        )}
        {advisorIds.map(id => (
          <AdvisorCard
            key={id}
            advisorId={id}
            advisor={lookupByAdvisorId.get(id)?.advisor ?? null}
            isLookupPending={lookupByAdvisorId.get(id)?.isPending ?? false}
            originalOverrides={
              ((originalConfig.advisors ?? {})[id] ?? {}) as Record<
                string,
                boolean | undefined
              >
            }
            targetOverrides={
              ((targetConfig.advisors ?? {})[id] ?? {}) as Record<
                string,
                boolean | undefined
              >
            }
            onEnqueueOp={onEnqueueOp}
            isSaving={isSaving}
          />
        ))}
      </div>
    </TooltipProvider>
  );
}

type AdvisorCardProps = {
  advisorId: string;
  advisor: Advisor | null;
  isLookupPending: boolean;
  originalOverrides: Record<string, boolean | undefined>;
  targetOverrides: Record<string, boolean | undefined>;
  onEnqueueOp: (op: FeatureFlagOp) => void;
  isSaving?: boolean;
};

function AdvisorCard({
  advisorId,
  advisor,
  isLookupPending,
  originalOverrides,
  targetOverrides,
  onEnqueueOp,
  isSaving,
}: AdvisorCardProps) {
  const [open, setOpen] = useState(false);

  const targetEntries = useMemo(
    () =>
      Object.entries(targetOverrides)
        .filter(([, v]) => typeof v === 'boolean')
        .sort(([a], [b]) => a.localeCompare(b)) as Array<[string, boolean]>,
    [targetOverrides],
  );

  const addableKeys = useMemo(
    () => KNOWN_FLAG_KEYS.filter(k => !(k in targetOverrides)),
    [targetOverrides],
  );

  const fullName = advisor
    ? [advisor.advisorFirstName, advisor.advisorLastName].filter(Boolean).join(' ')
    : '';

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
    >
      <Card className="border border-gray-200 p-0 shadow-sm">
        <div className="flex items-center">
          <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50">
            <div className="flex min-w-0 items-center gap-3">
              {open ? (
                <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
              ) : (
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="truncate font-mono text-xs text-gray-700">
                    {advisorId}
                  </span>
                  {fullName && <span className="truncate text-sm">{fullName}</span>}
                  {!fullName && !isLookupPending && (
                    <span className="text-muted-foreground text-xs italic">
                      not in database
                    </span>
                  )}
                </div>
                {advisor?.advisorEmail && (
                  <div className="text-muted-foreground truncate text-xs">
                    {advisor.advisorEmail}
                  </div>
                )}
              </div>
            </div>
            <Badge
              variant="secondary"
              className="shrink-0 text-xs"
            >
              {targetEntries.length} override{targetEntries.length === 1 ? '' : 's'}
            </Badge>
          </CollapsibleTrigger>

          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground mr-2 h-8 w-8 p-0 hover:text-red-700"
            onClick={() =>
              onEnqueueOp({ type: 'REMOVE_ADVISOR_SECTION', advisorId })
            }
            disabled={isSaving}
            aria-label="Remove all overrides for this advisor"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <CollapsibleContent>
          <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
            {targetEntries.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No overrides set for this advisor.
              </p>
            ) : (
              <div className="space-y-1">
                {targetEntries.map(([flagKey, value]) => {
                  const originalValue = originalOverrides[flagKey];
                  const isPending = value !== originalValue;
                  const editable = isKnownFlagKey(flagKey);
                  return (
                    <div
                      key={flagKey}
                      className="flex items-center justify-between gap-2 rounded border border-gray-100 bg-white px-3 py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{flagKey}</span>
                        {!editable && <LegacyFlagWarningIcon />}
                        {isPending && (
                          <Badge className="bg-amber-100 text-[10px] text-amber-900 hover:bg-amber-100">
                            pending
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={value}
                          disabled={!editable || isSaving}
                          onCheckedChange={checked =>
                            onEnqueueOp({
                              type: 'SET_ADVISOR_FLAG',
                              advisorId,
                              flagKey,
                              value: checked,
                            })
                          }
                        />
                        <span className="text-muted-foreground text-xs">
                          {value ? 'On' : 'Off'}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground h-6 w-6 p-0"
                          disabled={isSaving}
                          onClick={() =>
                            onEnqueueOp({
                              type: 'REMOVE_ADVISOR_FLAG',
                              advisorId,
                              flagKey,
                            })
                          }
                          aria-label="Remove override"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {addableKeys.length > 0 && (
              <div className="mt-3">
                <AddOverridePopover
                  addableKeys={addableKeys}
                  onAdd={(flagKey, value) =>
                    onEnqueueOp({
                      type: 'SET_ADVISOR_FLAG',
                      advisorId,
                      flagKey,
                      value,
                    })
                  }
                  disabled={isSaving}
                />
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

type AddOverridePopoverProps = {
  addableKeys: readonly string[];
  onAdd: (flagKey: string, value: boolean) => void;
  disabled?: boolean;
};

function AddOverridePopover({ addableKeys, onAdd, disabled }: AddOverridePopoverProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return addableKeys.slice(0, 50);
    return addableKeys.filter(k => k.toLowerCase().includes(q)).slice(0, 50);
  }, [addableKeys, query]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add override
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-2"
      >
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Filter flag keys…"
          className="mb-2 w-full rounded border border-gray-200 px-2 py-1 text-xs outline-none focus:border-gray-400"
        />
        <div className="max-h-64 overflow-auto">
          {matches.length === 0 && (
            <div className="text-muted-foreground px-2 py-4 text-center text-xs">
              No matches
            </div>
          )}
          {matches.map(key => (
            <div
              key={key}
              className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-gray-50"
            >
              <span className="truncate font-mono text-xs">{key}</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => {
                    onAdd(key, true);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  On
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => {
                    onAdd(key, false);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  Off
                </Button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
